import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, CreditCard, Clock, HeadphonesIcon, ArrowRight, CheckCircle, Award,
  ShieldCheck, Laptop, BadgeCheck, GraduationCap, UserPlus, BookOpen, FileCheck,
  Trophy, Heart, Briefcase, Monitor, Shield, Landmark, Users, BarChart2, Wifi,
} from 'lucide-react';
import { COURSES as MOCK_COURSES, STATS, CATEGORIES, FAQS } from '../data/mock';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import { normalizeCourse } from '../utils/normalizeCourse';
import { getInstalmentPlan } from '../utils/instalments';
import Seo from '../components/Seo';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATS_KEYS  = ['stats.courses', 'stats.levels', 'stats.portal', 'stats.online'];
const STATS_ICONS = [BookOpen, BarChart2, Clock, Wifi];

const HOME_FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.slice(0, 6).map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
};

const CATEGORY_META = [
  { name: 'Health & Social Care',        icon: Heart,        color: 'text-rose-500',   bg: 'bg-rose-50'   },
  { name: 'Business & Management',        icon: Briefcase,    color: 'text-blue-600',   bg: 'bg-blue-50'   },
  { name: 'Education & Training',         icon: GraduationCap,color: 'text-violet-600', bg: 'bg-violet-50' },
  { name: 'IT & Technology',              icon: Monitor,      color: 'text-cyan-600',   bg: 'bg-cyan-50'   },
  { name: 'Occupational Health & Safety', icon: Shield,       color: 'text-amber-600',  bg: 'bg-amber-50'  },
  { name: 'Law',                          icon: Landmark,     color: 'text-slate-600',  bg: 'bg-slate-100' },
  { name: 'Early Years & Childcare',      icon: Users,        color: 'text-green-600',  bg: 'bg-green-50'  },
];

// ── Hero ─────────────────────────────────────────────────────────────────────
function HeroSection() {
  const { t } = useSiteLanguage();
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(query.trim() ? `/courses?search=${encodeURIComponent(query.trim())}` : '/courses');
  };

  return (
    <section className="relative min-h-[580px] lg:min-h-[640px] flex flex-col overflow-hidden">
      {/* Full-width background image */}
      <img
        src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1600&h=700&fit=crop&q=85"
        alt="Students studying"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      {/* Dark overlay to deepen the image */}
      <div className="absolute inset-0 bg-black/45" />

      {/* Content */}
      <div className="relative flex-1 max-w-7xl mx-auto w-full px-4 py-16 lg:py-24 flex items-center">
        {/* White content box */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10 max-w-xl w-full">
          {/* Seal + eyebrow row */}
          <div className="flex items-center gap-4 mb-5">
            <img
              src="/images/atlas_seal_badge.png"
              alt="Atlas Academy seal"
              className="w-14 h-14 object-contain shrink-0"
            />
            <div className="inline-flex items-center gap-2 bg-[#EEF0FF] rounded-full px-4 py-1.5">
              <Award size={13} className="text-[#4B286D]" />
              <span className="text-[#4B286D] text-xs font-semibold">{t('home.hero.eyebrow')}</span>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-[1.15] tracking-tight text-[#3A343A] mb-4">
            {t('home.hero.title1')}
            <span className="text-[#DE00A5]"> {t('home.hero.titleHighlight')}</span>
            <br />{t('home.hero.title2')}
          </h1>

          <p className="text-gray-500 text-base leading-relaxed mb-7">
            {t('home.hero.desc1')}
          </p>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="flex mb-6 rounded-2xl overflow-hidden border border-[#EDEDEE] shadow-sm">
            <div className="flex-1 relative bg-white">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
              <input
                type="text"
                placeholder={t('home.hero.searchPlaceholder')}
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 text-gray-800 placeholder-gray-400 text-sm focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="bg-[#DE00A5] hover:bg-[#B80089] text-white px-6 py-3.5 text-sm font-bold transition-colors whitespace-nowrap"
            >
              {t('home.hero.searchBtn')}
            </button>
          </form>

          {/* Quick category pills */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.filter(c => c !== 'All Courses').slice(0, 5).map(cat => (
              <Link
                key={cat}
                to={`/courses?category=${encodeURIComponent(cat)}`}
                className="text-xs text-[#4B286D] bg-[#F3ECF9] hover:bg-[#4B286D] hover:text-white border border-[#4B286D]/20 px-3.5 py-1.5 rounded-full transition-all font-medium"
              >
                {cat}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="relative bg-black/50 backdrop-blur-sm border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-5 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
          {[
            { icon: CreditCard,     title: t('home.feature.payment.title'), desc: t('home.feature.payment.desc') },
            { icon: Clock,          title: t('home.feature.pace.title'),    desc: t('home.feature.pace.desc')    },
            { icon: HeadphonesIcon, title: t('home.feature.tutor.title'),   desc: t('home.feature.tutor.desc')   },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-center gap-3 px-6 py-3 sm:py-0 first:pl-0">
              <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                <Icon size={16} className="text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{title}</p>
                <p className="text-white/50 text-xs mt-0.5">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Stats ─────────────────────────────────────────────────────────────────────
function StatsSection() {
  const { t } = useSiteLanguage();
  return (
    <section className="bg-white py-12 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:divide-x divide-gray-100">
          {STATS.map((stat, i) => {
            const Icon = STATS_ICONS[i];
            return (
              <div key={i} className="text-center px-4 group cursor-default">
                <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-[#F3ECF9] group-hover:bg-[#4B286D] transition-colors duration-300 flex items-center justify-center">
                  <Icon size={22} className="text-[#4B286D] group-hover:text-white transition-colors duration-300" />
                </div>
                <p className="text-3xl font-bold text-[#4B286D]">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1 font-medium">{t(STATS_KEYS[i], stat.label)}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Shared Course Card (FutureLearn style) ────────────────────────────────────
function CourseCard({ course }) {
  const { t } = useSiteLanguage();
  const plan = getInstalmentPlan(course);
  return (
    <Link
      to={`/courses/${course.slug}`}
      className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-[#E5E5E5] hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
    >
      {/* Image */}
      <div className="relative h-44 overflow-hidden bg-gray-100 shrink-0">
        <img
          src={course.image}
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {course.onSale && (
          <span className="absolute top-3 left-3 bg-[#DE00A5] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
            {t('shared.badge.sale')}
          </span>
        )}
      </div>

      {/* Card body */}
      <div className="p-5 flex flex-col flex-1">
        {/* Awarding body + duration */}
        <div className="flex items-center gap-2 mb-2.5">
          <span className="text-xs font-semibold text-[#4B286D] bg-[#F3ECF9] px-2.5 py-1 rounded-full">
            {course.awardingBody}
          </span>
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Clock size={11} />{course.duration}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-bold text-[#1A1A2E] text-sm leading-snug mb-auto group-hover:text-[#4B286D] transition-colors line-clamp-2 min-h-[2.5rem]">
          {course.title}
        </h3>

        {/* Level pill */}
        <div className="mt-3 mb-3">
          <span className="text-xs text-gray-500 border border-gray-200 px-2.5 py-1 rounded-full">
            {course.level}
          </span>
        </div>

        {/* Price + CTA */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div>
            <span className="text-[#1A1A2E] font-extrabold text-xl">£{course.price}</span>
            {course.onSale && (
              <span className="text-gray-400 line-through text-xs ml-1.5">£{course.originalPrice}</span>
            )}
            {plan && (
              <p className="text-xs text-gray-400 mt-0.5">
                {t('shared.instalments.short', 'or {count} × £{amount}')
                  .replace('{count}', plan.count)
                  .replace('{amount}', plan.perInstalment)}
              </p>
            )}
          </div>
          <span className="shrink-0 text-xs font-bold bg-[#DE00A5] text-white px-3.5 py-1.5 rounded-full group-hover:bg-[#B80089] transition-colors">
            Enrol →
          </span>
        </div>
      </div>
    </Link>
  );
}

// ── Featured Courses ──────────────────────────────────────────────────────────
function FeaturedCoursesSection() {
  const { t } = useSiteLanguage();
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    axios.get(`${API}/courses`)
      .then(res => setCourses(res.data.map(normalizeCourse).filter(c => c.featured)))
      .catch(() => setCourses(MOCK_COURSES.filter(c => c.featured)));
  }, []);

  return (
    <section className="py-20 bg-[#F7F7F7]">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[#4B286D] font-semibold text-sm mb-1">{t('home.courses.eyebrow')}</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#1A1A2E]">{t('home.courses.title')}</h2>
          </div>
          <Link
            to="/courses"
            className="hidden sm:flex items-center gap-1.5 text-[#4B286D] hover:text-[#DE00A5] font-bold text-sm transition-colors group"
          >
            {t('home.courses.browseAll')} <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        {courses.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl h-80 animate-pulse border border-gray-100" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.slice(0, 6).map(course => (
              <CourseCard key={course.id || course.slug} course={course} />
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 bg-[#4B286D] hover:bg-[#3B1D57] text-white px-8 py-3.5 rounded-full font-bold text-sm transition-colors shadow-md"
          >
            {t('home.courses.viewAll')} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Popular Categories ────────────────────────────────────────────────────────
function PopularCategoriesSection() {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    axios.get(`${API}/courses`)
      .then(res => {
        const c = {};
        res.data.forEach(course => { c[course.category] = (c[course.category] || 0) + 1; });
        setCounts(c);
      })
      .catch(() => {
        const c = {};
        MOCK_COURSES.forEach(course => { c[course.category] = (c[course.category] || 0) + 1; });
        setCounts(c);
      });
  }, []);

  return (
    <section className="bg-white py-20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-[#4B286D] font-semibold text-sm mb-1">Explore by Subject</p>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#1A1A2E]">Browse by Category</h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {CATEGORY_META.map(({ name, icon: Icon, color, bg }) => (
            <Link
              key={name}
              to={`/courses?category=${encodeURIComponent(name)}`}
              className="group bg-white hover:bg-[#4B286D] border border-[#E5E5E5] hover:border-[#4B286D] rounded-2xl p-5 transition-all duration-300 hover:shadow-lg"
            >
              <div className={`w-11 h-11 ${bg} group-hover:bg-white/15 rounded-xl flex items-center justify-center mb-3.5 transition-colors`}>
                <Icon size={20} className={`${color} group-hover:text-white transition-colors`} />
              </div>
              <h3 className="font-bold text-[#1A1A2E] text-sm group-hover:text-white transition-colors leading-snug">{name}</h3>
              {counts[name] != null && (
                <p className="text-xs text-gray-400 mt-1 group-hover:text-purple-200 transition-colors">
                  {counts[name]} course{counts[name] !== 1 ? 's' : ''}
                </p>
              )}
            </Link>
          ))}

          {/* View all tile */}
          <Link
            to="/courses"
            className="group bg-[#4B286D] hover:bg-[#3B1D57] rounded-2xl p-5 transition-all duration-300 hover:shadow-lg flex flex-col justify-between"
          >
            <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center mb-3.5">
              <Search size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">View All Courses</h3>
              <p className="text-xs text-purple-200 mt-1">{MOCK_COURSES.length}+ available</p>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Career / Why Choose Us ─────────────────────────────────────────────────────
function CareerSection() {
  const { t } = useSiteLanguage();
  return (
    <section className="bg-[#F7F7F7] py-20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-3xl p-8 lg:p-12 border border-[#E5E5E5] flex flex-col lg:flex-row items-center gap-10">
          <div className="flex-1">
            <p className="text-[#4B286D] font-semibold text-sm mb-2">{t('home.career.eyebrow')}</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#1A1A2E] mb-4 leading-tight">
              {t('home.career.title')}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6 text-sm">
              {t('home.career.desc')}
            </p>
            <ul className="space-y-3 mb-8">
              {[
                t('home.career.point1'),
                t('home.career.point2'),
                t('home.career.point3'),
                t('home.career.point4'),
              ].map(item => (
                <li key={item} className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-5 h-5 bg-[#F3ECF9] rounded-full flex items-center justify-center shrink-0">
                    <CheckCircle size={13} className="text-[#4B286D]" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#DE00A5] hover:bg-[#B80089] text-white px-6 py-3 rounded-full font-bold text-sm transition-colors shadow-sm"
            >
              {t('home.career.cta')} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="w-full lg:w-[400px] shrink-0">
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=640&h=480&fit=crop&q=80"
              alt="Career guidance"
              className="rounded-2xl w-full h-64 lg:h-72 object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Why Atlas ─────────────────────────────────────────────────────────────────
function WhyAtlasSection() {
  const { t } = useSiteLanguage();
  const benefits = [
    { icon: ShieldCheck,    title: t('home.why.accredited.title', 'Ofqual-Regulated Awarding Bodies'),   desc: t('home.why.accredited.desc', 'Qualifications awarded by OTHM and QUALIFI, recognised by employers and universities across the UK.') },
    { icon: HeadphonesIcon, title: t('home.why.tutor.title',     'Dedicated Tutor Support'),             desc: t('home.why.tutor.desc',     'Every learner is assigned a specialist tutor for feedback, guidance, and encouragement throughout the course.') },
    { icon: Laptop,         title: t('home.why.online.title',    '100% Online Study'),                   desc: t('home.why.online.desc',    'All materials, assignments, and feedback delivered through our online portal — no classroom attendance required.') },
    { icon: CreditCard,     title: t('home.why.payment.title',   'Flexible Payment Plans'),              desc: t('home.why.payment.desc',   'Pay in full or spread the cost with a clear instalment plan shown before you enrol. No hidden charges.') },
    { icon: BadgeCheck,     title: t('home.why.recognised.title','Employer-Valued Qualifications'),      desc: t('home.why.recognised.desc','Vocational diplomas from Level 2 to Level 7, mapped to the Regulated Qualifications Framework (RQF).') },
    { icon: GraduationCap,  title: t('home.why.progression.title','University Progression'),             desc: t('home.why.progression.desc','Higher-level diplomas can lead to university top-up degrees. Ask our advisers about progression routes.') },
  ];

  return (
    <section className="bg-white py-20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-[#4B286D] font-semibold text-sm mb-1">{t('home.why.eyebrow', 'Why Choose Us')}</p>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#1A1A2E]">{t('home.why.title', 'Why Study With Atlas Academy')}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {benefits.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="rounded-2xl p-6 border border-[#E5E5E5] hover:border-[#4B286D]/25 hover:shadow-md transition-all duration-300"
            >
              <div className="w-11 h-11 bg-[#F3ECF9] rounded-xl flex items-center justify-center mb-4">
                <Icon size={20} className="text-[#4B286D]" />
              </div>
              <h3 className="font-bold text-[#1A1A2E] text-base mb-2">{title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Full-Width Banner ─────────────────────────────────────────────────────────
function FullWidthBannerSection() {
  return (
    <section className="relative bg-gradient-to-r from-[#4B286D] to-[#3B1D57] py-24 lg:py-32 overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full translate-x-1/3 -translate-y-1/2 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#DE00A5]/10 rounded-full -translate-x-1/4 translate-y-1/3 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4">
        <div className="max-w-lg">
          <p className="text-[#DE00A5] font-bold text-sm uppercase tracking-widest mb-3">Flexible Online Learning</p>
          <h2 className="text-3xl lg:text-5xl font-bold text-white leading-tight mb-5">
            Learn at Your Own Pace,<br />On Your Own Schedule
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-md">
            Accredited distance learning programmes designed around your work, family, and everyday commitments.
          </p>
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 bg-[#DE00A5] hover:bg-[#B80089] text-white px-8 py-4 rounded-full font-bold text-sm transition-colors shadow-lg"
          >
            Explore Courses <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Roadmap ───────────────────────────────────────────────────────────────────
function RoadmapSection() {
  const { t } = useSiteLanguage();
  const steps = [
    { icon: UserPlus,   title: t('home.roadmap.step1.title', 'Apply Online'),      desc: t('home.roadmap.step1.desc', 'Choose your course and complete our short application form.') },
    { icon: CreditCard, title: t('home.roadmap.step2.title', 'Enrol & Pay'),        desc: t('home.roadmap.step2.desc', 'Pay in full or set up a payment plan, then get instant portal access.') },
    { icon: BookOpen,   title: t('home.roadmap.step3.title', 'Study Flexibly'),     desc: t('home.roadmap.step3.desc', 'Work through your materials at your own pace, around your life.') },
    { icon: FileCheck,  title: t('home.roadmap.step4.title', 'Submit Assignments'), desc: t('home.roadmap.step4.desc', 'Get tutor feedback on every assignment until you pass each unit.') },
    { icon: Trophy,     title: t('home.roadmap.step5.title', 'Get Qualified'),      desc: t('home.roadmap.step5.desc', 'Receive your certificate from the awarding body and progress your career.') },
  ];

  return (
    <section className="bg-[#F7F7F7] py-20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-14">
          <p className="text-[#4B286D] font-semibold text-sm mb-1">{t('home.roadmap.eyebrow', 'How It Works')}</p>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#1A1A2E]">{t('home.roadmap.title', 'Your Journey to a UK Qualification')}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-4">
          {steps.map(({ icon: Icon, title, desc }, i) => (
            <div key={title} className="relative text-center px-2">
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[calc(50%+2.5rem)] w-[calc(100%-5rem)] border-t-2 border-dashed border-[#4B286D]/20" />
              )}
              <div className="relative w-16 h-16 mx-auto bg-[#4B286D] hover:bg-[#DE00A5] transition-colors duration-300 rounded-2xl flex items-center justify-center mb-5 shadow-md cursor-default">
                <Icon size={24} className="text-white" />
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-[#DE00A5] text-white text-xs font-extrabold rounded-full flex items-center justify-center shadow">{i + 1}</span>
              </div>
              <h3 className="font-bold text-[#1A1A2E] text-sm mb-2">{title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-2 border-2 border-[#4B286D] text-[#4B286D] hover:bg-[#4B286D] hover:text-white px-6 py-2.5 rounded-full font-bold text-sm transition-all"
          >
            {t('home.roadmap.cta', 'Learn more about how it works')} <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Awarding Bodies ───────────────────────────────────────────────────────────
function AwardingBodiesSection() {
  const { t } = useSiteLanguage();
  return (
    <section className="bg-white py-16 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 text-center">
        <p className="text-[#4B286D] font-semibold text-sm mb-1">Accreditation</p>
        <h2 className="text-2xl font-bold text-[#1A1A2E] mb-10">{t('home.awarding.title', 'Our Awarding Bodies')}</h2>
        <div className="flex items-center justify-center gap-12 flex-wrap">
          <div className="flex flex-col items-center gap-3">
            <div className="w-52 h-32 bg-[#4B286D] rounded-2xl shadow-md p-4 flex items-center justify-center hover:shadow-xl transition-shadow">
              <img src="/images/othm-logo.svg" alt="OTHM Awarding Body" className="w-full h-auto max-h-20 object-contain" />
            </div>
            <p className="text-xs text-gray-500 font-medium">{t('home.awarding.regulatedBody')}</p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="w-52 h-32 bg-white rounded-2xl border border-[#E5E5E5] shadow-md p-1 flex items-center justify-center hover:shadow-xl transition-shadow">
              <img src="/images/qualifi-logo.png" alt="Qualifi Awarding Body" className="w-full h-full object-contain" />
            </div>
            <p className="text-xs text-gray-500 font-medium">{t('home.awarding.regulatedBody')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQSection() {
  const { t } = useSiteLanguage();
  return (
    <section className="bg-[#F7F7F7] py-20">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-10">
          <p className="text-[#4B286D] font-semibold text-sm mb-1">{t('home.faq.eyebrow', 'Common Questions')}</p>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#1A1A2E]">{t('home.faq.title', 'Frequently Asked Questions')}</h2>
        </div>
        <Accordion type="single" collapsible className="bg-white rounded-2xl border border-[#E5E5E5] shadow-sm divide-y divide-gray-100 overflow-hidden">
          {FAQS.slice(0, 6).map(faq => (
            <AccordionItem key={faq.id} value={faq.id} className="border-0 px-6">
              <AccordionTrigger className="text-left text-sm font-bold text-[#1A1A2E] hover:no-underline hover:text-[#4B286D] py-5">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-gray-600 leading-relaxed pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <p className="text-center text-sm text-gray-500 mt-6">
          {t('home.faq.more', 'Still have questions?')}{' '}
          <Link to="/contact" className="text-[#4B286D] font-bold hover:underline">
            {t('home.faq.contactLink', 'Speak to an adviser')}
          </Link>
        </p>
      </div>
    </section>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────
function CTASection() {
  const { t } = useSiteLanguage();
  return (
    <section className="bg-[#4B286D] py-20 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full translate-x-1/3 -translate-y-1/2 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#DE00A5]/10 rounded-full -translate-x-1/3 translate-y-1/3 blur-3xl pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-4 text-center">
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
          {t('home.cta.title')}
        </h2>
        <p className="text-white/65 max-w-xl mx-auto mb-10 text-lg leading-relaxed">
          {t('home.cta.desc')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/courses"
            className="inline-flex items-center justify-center gap-2 bg-[#DE00A5] hover:bg-[#B80089] text-white px-10 py-4 rounded-full font-bold text-sm transition-colors shadow-lg"
          >
            {t('home.cta.browseCourses')} <ArrowRight size={16} />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/25 hover:border-white/40 text-white px-10 py-4 rounded-full font-bold text-sm transition-all"
          >
            {t('home.cta.speakAdviser')}
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Home() {
  return (
    <>
      <Seo
        title="Accredited Online Courses & UK Qualifications"
        description="Study flexible, accredited OTHM and QUALIFI diplomas online with Atlas Academy. Levels 2–7, dedicated tutor support, and clear payment plans."
        path="/"
        jsonLd={HOME_FAQ_JSONLD}
      />
      <HeroSection />
      <StatsSection />
      <FeaturedCoursesSection />
      <PopularCategoriesSection />
      <CareerSection />
      <WhyAtlasSection />
      <FullWidthBannerSection />
      <RoadmapSection />
      <AwardingBodiesSection />
      <FAQSection />
      <CTASection />
    </>
  );
}
